import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  Paper
} from "@mui/material";

import { useLanguage } from "../context/LanguageContext.js";
import { useGroupe } from "../context/GroupeContext.js";
import { logDev } from "../utils/logger.js";

const PagePaiement = ({ headerHeight = 0 }) => {
  const { groupeId: groupeIdFromUrl } = useParams();
  const [params] = useSearchParams();

  const groupeId =
    groupeIdFromUrl ||
    params.get("groupeId") ||
    sessionStorage.getItem("groupeId");

  const { t, lang } = useLanguage();
  const { setGroupe } = useGroupe();
  const navigate = useNavigate();

  const { groupeId: contexteId, isReady } = useGroupe();

  const [pendingRedirectId, setPendingRedirectId] = useState(null);
  const [recup, setRecup] = useState({});
  const [groupe, setGroupeLocal] = useState(null);
  const [quota, setQuota] = useState(null);
  const [estGroupeEssai, setEstGroupeEssai] = useState(null);

  const nom =
    params.get("nom") ||
    sessionStorage.getItem("nom");

  const email = params.get("email");
  const tel = params.get("tel");

  const offres = [
    {
      key: "forfait20",
      type: "forfait20",
      label: t("Package20Contacts"),
      price: 6,
      duration: t("NewGroup20Contacts")
    },
    {
      key: "add5",
      type: "add5",
      label: t("Add5Contacts"),
      price: 1,
      duration: t("Add5ContactsToGroup")
    }
  ];

  logDev("[PagePaiement] paramètres reçus", {
    groupeId,
    nom
  });

  // Stocke groupeId dans sessionStorage
  useEffect(() => {
    if (groupeId && groupeId !== "undefined") {
      sessionStorage.setItem("groupeId", groupeId);
    }
  }, [groupeId]);

  // Charge le contexte de paiement
  useEffect(() => {
    if (!groupeId || groupeId === "undefined") return;

    const fetchContextePaiement = async () => {
      try {
        logDev(
          "📥 Requête envoyée vers /paiement/context/:groupeId",
          { groupeId }
        );

        const res = await axios.get(
          `${process.env.REACT_APP_API}/paiement/context/${groupeId}`
        );

        logDev(
          "📊 Contexte reçu dans PagePaiement",
          res.data
        );

        setGroupeLocal(res.data.groupe);
        setQuota(res.data.quota);

        if (res.data.groupe?._id) {
          sessionStorage.setItem(
            "groupeId",
            res.data.groupe._id
          );
        }

        if (res.data.utilisateurId) {
          sessionStorage.setItem(
            "utilisateurId",
            res.data.utilisateurId
          );
        }

        const estEssai =
          res.data.groupe?.abonnementActif === false &&
          res.data.groupe?.sondageVerrouille === true &&
          res.data.quota?.groupesMax === 1 &&
          res.data.quota?.contactsMax <= 20 &&
          res.data.groupe?.prolongationGratuite !== true;

        setEstGroupeEssai(estEssai);

        logDev("🔍 Résultat estGroupeEssai", {
          estGroupeEssai: estEssai,
          abonnementActif:
            res.data.groupe?.abonnementActif,
          sondageVerrouille:
            res.data.groupe?.sondageVerrouille,
          groupesMax:
            res.data.quota?.groupesMax,
          contactsMax:
            res.data.quota?.contactsMax,
          prolongationGratuite:
            res.data.groupe?.prolongationGratuite
        });
      } catch (err) {
        logDev("❌ Erreur contexte paiement", {
          message: err.message
        });
      }
    };

    fetchContextePaiement();
  }, [groupeId]);

  // Contexte RGPD si aucun groupeId mais présence d'un hash
  useEffect(() => {
    const hash =
      params.get("hash") ||
      sessionStorage.getItem("hashCreateur");

    if (!groupeId && hash) {
      axios
        .get(
          `${process.env.REACT_APP_API}/paiement/context-par-utilisateur/${hash}`
        )
        .then(res => {
          logDev(
            "📊 Contexte RGPD reçu",
            res.data
          );

          setGroupeLocal(res.data.groupe);
          setQuota(res.data.quota);
          setEstGroupeEssai(res.data.estEssai);

          if (res.data.groupe?._id) {
            sessionStorage.setItem(
              "groupeId",
              res.data.groupe._id
            );

            logDev(
              "💾 groupeId stocké depuis hash",
              res.data.groupe._id
            );
          }
        })
        .catch(err => {
          logDev("❌ Erreur contexte RGPD", {
            message: err.message
          });

          navigate("/erreur-paiement");
        });
    }
  }, [groupeId, params, navigate]);

  // Redirection après création d'un nouveau groupe
  useEffect(() => {
    if (
      isReady &&
      pendingRedirectId &&
      contexteId === pendingRedirectId
    ) {
      logDev(
        "✅ Contexte prêt, redirection vers contacts",
        { groupeId: contexteId }
      );

      navigate(
        `/contacts/${contexteId}?upgrade=success`
      );
    }
  }, [
    isReady,
    contexteId,
    pendingRedirectId,
    navigate
  ]);

  const estProcheExpiration =
    groupe?.expiration &&
    new Date(groupe.expiration) - new Date() <
      7 * 24 * 60 * 60 * 1000;

  const handleUpgrade = async (type) => {
    const mode =
      recup[type]
        ? "recuperation"
        : "nouveau";

    try {
      logDev("🧪 Type choisi", type);

      // Achat d'un nouveau groupe de 20 contacts
      if (type === "forfait20") {
        const utilisateurId =
          sessionStorage.getItem("utilisateurId") ||
          params.get("utilisateurId");

        if (!utilisateurId) {
          logDev(
            "❌ Aucun utilisateurId pour paiement quota"
          );
          return;
        }

        const res = await axios.post(
          `${process.env.REACT_APP_API}/paiement/upgrade/quota`,
          {
            utilisateurId,
            groupeId,
            type,
            recuperation: !!recup[type],
            lang
          }
        );

        if (res.data.url) {
          window.location.href = res.data.url;
        } else if (res.data.success) {
          navigate("/creation?quota=success");
        } else {
          logDev(
            "❌ Réponse inattendue quota",
            res.data
          );
        }

        return;
      }

      // Ajouter 5 contacts au groupe actuel
      if (type === "add5") {
        const res = await axios.post(
          `${process.env.REACT_APP_API}/paiement/upgrade`,
          {
            groupeId: groupeId,
            type: "add5",
            mode: "nouveau",
            recupererContacts: false,
            lang
          }
        );

        if (res.data.url) {
          window.location.href = res.data.url;
        } else if (res.data.success) {
          navigate(
            `/contacts/${groupeId}?upgrade=add5`
          );
        }

        return;
      }

      if (!groupeId) {
        logDev(
          "❌ Aucun groupeId dans handleUpgrade"
        );
        return;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API}/paiement/upgrade`,
        {
          groupeId: groupeId,
          email,
          tel,
          type,
          mode,
          recupererContacts: !!recup[type],
          lang
        }
      );

      logDev(
        "📦 Réponse backend",
        res.data
      );

      if (res.data.url) {
        window.location.href = res.data.url;
      } else if (res.data.groupeId) {
        logDev(
          "🎉 Upgrade gratuit réussi",
          {
            groupeId: res.data.groupeId
          }
        );

        sessionStorage.setItem(
          "groupeId",
          res.data.groupeId
        );

        sessionStorage.setItem(
          "role",
          "admin"
        );

        sessionStorage.setItem(
          "nom",
          res.data.nom || groupe?.nom || ""
        );

        setGroupe(
          res.data.groupeId,
          "admin"
        );

        sessionStorage.setItem(
          "lastUpgradeType",
          type
        );

        if (res.data.utilisateurId) {
          sessionStorage.setItem(
            "utilisateurId",
            res.data.utilisateurId
          );
        }

        if (
          groupeId !== res.data.groupeId
        ) {
          sessionStorage.removeItem(
            "groupeId_initial"
          );
        }

        setPendingRedirectId(
          res.data.groupeId
        );
      } else {
        logDev(
          "❌ Réponse inattendue après upgrade",
          res.data
        );
      }
    } catch (err) {
      console.error(
        "Erreur upgrade :",
        err
      );

      logDev(
        "❌ Erreur dans handleUpgrade",
        {
          message: err.message
        }
      );
    }
  };

  const phrasesIntro = {
    forfait20: t("Intro20Contacts"),
    add5: t("IntroAdd5Contacts")
  };

  logDev(
    "🧪 Groupe complet",
    groupe
  );

  return (
    <Box
      sx={{
        p: 4,
        mt: `${headerHeight}px`
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
      >
        {t("GroupOptions")}{" "}
        <strong>{nom}</strong>
      </Typography>

      {groupe?.abonnement?.type && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#e3f2fd",
            borderLeft:
              "6px solid #2196f3",
            borderRadius: 1
          }}
        >
          <Typography variant="body1">
            📦 {t("CurrentSubscription")}{" "}
            <strong>
              {t(
                groupe.abonnement.type
              )}
            </strong>
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
          >
            {t("ValidUntil")}{" "}
            <strong>
              {groupe.expiration
                ? new Date(
                    groupe.expiration
                  ).toLocaleDateString(
                    "fr-FR"
                  )
                : t(
                    "NoExpirationDefined"
                  )}
            </strong>
          </Typography>
        </Box>
      )}

      {estProcheExpiration && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#fff8f0",
            borderLeft:
              "6px solid #ff9800",
            borderRadius: 1
          }}
        >
          <Typography variant="body1">
            ⏳ {t("GroupExpirationDate")}{" "}
            <strong>
              {new Date(
                groupe.expiration
              ).toLocaleDateString(
                "fr-FR"
              )}
            </strong>
            . {t("ConsiderEntendingIt")}.
          </Typography>
        </Box>
      )}

      {groupe?.prolongationGratuite ===
        true && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#fff3e0",
            borderLeft:
              "6px solid #ffa726",
            borderRadius: 1
          }}
        >
          <Typography variant="body1">
            🎁 {t(
              "GroupExtensionFreeUsed"
            )}
          </Typography>
        </Box>
      )}

      {estGroupeEssai &&
        !groupe?.supprime && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f0f4ff",
              borderLeft: `6px solid ${
                groupe?.couleur ||
                "#1976d2"
              }`,
              borderRadius: 1
            }}
          >
            <Typography variant="body1">
              {groupe?.emoji || "🎁"}{" "}
              {t("TestGroupMessage")}
            </Typography>
          </Box>
        )}

      {quota?.groupesUtilises >=
        quota?.groupesMax &&
        groupe?.sondageVerrouille && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#ffebee",
              borderLeft:
                "6px solid #f44336",
              borderRadius: 1
            }}
          >
            <Typography variant="body1">
              ❌{" "}
              {t(
                "NoticeQuotaUsedAndGroupDeleted"
              )}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 0.5 }}
            >
              {t(
                "YouHaveReachedYourQuota"
              )}{" "}
              —{" "}
              {t(
                "YourGroupWasDeleted"
              )}
            </Typography>
          </Box>
        )}

      <Stack
        spacing={2}
        sx={{ mt: 3 }}
      >
        {offres.map(offre => (
          <Paper
            key={offre.key}
            sx={{
              p: 2,
              display: "flex",
              flexDirection:
                "column",
              gap: 2
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {phrasesIntro[
                offre.key
              ]}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "flex-start",
                flexWrap: "wrap",
                gap: 2
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 200
                }}
              >
                <Typography
                  variant="subtitle1"
                >
                  {offre.label} –{" "}
                  {offre.duration}{" "}
                  {offre.price > 0
                    ? `– ${offre.price} €`
                    : t("Free")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection:
                    "column",
                  alignItems:
                    "flex-end",
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    handleUpgrade(
                      offre.type
                    )
                  }
                >
                  {t(
                    "ChooseThisOption"
                  )}
                </Button>

                {offre.type ===
                  "forfait20" && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          !!recup[
                            offre.type
                          ]
                        }
                        onChange={e =>
                          setRecup(
                            prev => ({
                              ...prev,
                              [offre.type]:
                                e.target
                                  .checked
                            })
                          )
                        }
                      />
                    }
                    label={t(
                      "RecoverMyContacts"
                    )}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default PagePaiement;